(() => {
  const SELECTORS = {
    root: 'search-form',
    input: '[data-js-search-input]',
    results: '[data-js-search-results]',
    overlayId: (input) => input?.dataset?.jsFocusOverlay,
    overlayById: (id) => (id ? document.getElementById(id) : null),
  };

  const debounce = (fn, wait = 180) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const escapeHtml = (str = "") =>
    str.replace(/[&<>"']/g, (m) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#039;" }[m]));

  const moneyFromHtml = (html) => html || "";

  function setActive(overlay, resultsEl, active) {
    if (!overlay || !resultsEl) return;
    overlay.style.display = active ? "block" : "none";
    overlay.classList.toggle("active", !!active);
    resultsEl.classList.toggle("active", !!active);
    resultsEl.style.display = active ? "block" : "none";
  }

  function closeAll(formEl) {
    const input = formEl.querySelector(SELECTORS.input);
    const resultsEl = formEl.querySelector(SELECTORS.results);
    const overlay = SELECTORS.overlayById(SELECTORS.overlayId(input));
    setActive(overlay, resultsEl, false);
  }

  function buildItem({ url, title, image, vendor, priceHtml }) {
    return `
      <a class="cs-item" href="${url}">
        <div class="cs-thumb">
          ${image ? `<img src="${image}" alt="${escapeHtml(title)}" loading="lazy">` : `<span class="cs-thumb__ph"></span>`}
        </div>
        <div class="cs-meta">
          <div class="cs-title">${escapeHtml(title)}</div>
          <div class="cs-sub">
            ${vendor ? `<span class="cs-vendor">${escapeHtml(vendor)}</span>` : ``}
            ${priceHtml ? `<span class="cs-price">${moneyFromHtml(priceHtml)}</span>` : ``}
          </div>
        </div>
      </a>
    `;
  }

  function renderResults(resultsEl, q, json) {
    const products = json?.resources?.results?.products || [];
    const collections = json?.resources?.results?.collections || [];
    const pages = json?.resources?.results?.pages || [];
    const articles = json?.resources?.results?.articles || [];

    const hasAnything = products.length || collections.length || pages.length || articles.length;

    const productsHtml = products.slice(0, 6).map(p =>
      buildItem({
        url: p.url,
        title: p.title,
        image: p.image,
        vendor: p.vendor,
        priceHtml: p.price,
      })
    ).join("");

    const quickLinks = [
      { label: `Ver todos los resultados`, url: `/search?q=${encodeURIComponent(q)}` }
    ];

    const linksHtml = quickLinks.map(l => `<a class="cs-link" href="${l.url}">${escapeHtml(l.label)}</a>`).join("");

    resultsEl.innerHTML = `
      <div class="cs-panel">
        ${hasAnything ? `
          <div class="cs-head">
            <span class="cs-head__label">Resultados</span>
            <span class="cs-head__q">${escapeHtml(q)}</span>
          </div>

          ${products.length ? `
            <div class="cs-grid">
              ${productsHtml}
            </div>
          ` : `
            <div class="cs-empty">
              No encontramos resultados para <strong>${escapeHtml(q)}</strong>.
            </div>
          `}

          <div class="cs-foot">
            ${linksHtml}
          </div>
        ` : `
          <div class="cs-empty">
            No encontramos resultados para <strong>${escapeHtml(q)}</strong>.
          </div>
          <div class="cs-foot">
            ${linksHtml}
          </div>
        `}
      </div>
    `;
  }

  async function fetchPredictive(q) {
    // Shopify devuelve HTML por defecto; pedimos JSON con "resources" si la tienda lo permite.
    // Si no, fallback: parseamos HTML (pero intentamos JSON primero).
    const url = `${window.Shopify?.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,vendor&section_id=predictive-search`;
    const res = await fetch(url, { headers: { "Accept": "application/json" } });

    // Si Shopify no devuelve JSON, hará fallback a HTML o dará error.
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return res.json();
    }

    // Fallback: intenta leer como texto y devolver estructura vacía (para no romper)
    await res.text();
    return { resources: { results: { products: [] } } };
  }

  const onInput = debounce(async (formEl) => {
    const input = formEl.querySelector(SELECTORS.input);
    const resultsEl = formEl.querySelector(SELECTORS.results);
    const overlay = SELECTORS.overlayById(SELECTORS.overlayId(input));

    if (!input || !resultsEl) return;

    const q = (input.value || "").trim();

    if (q.length < 2) {
      setActive(overlay, resultsEl, false);
      return;
    }

    setActive(overlay, resultsEl, true);
    resultsEl.classList.add("cs-loading");

    try {
      const json = await fetchPredictive(q);
      renderResults(resultsEl, q, json);
    } catch (e) {
      resultsEl.innerHTML = `
        <div class="cs-panel">
          <div class="cs-empty">
            Hubo un problema cargando la búsqueda. Intenta de nuevo.
          </div>
          <div class="cs-foot">
            <a class="cs-link" href="/search?q=${encodeURIComponent(q)}">Ver todos los resultados</a>
          </div>
        </div>
      `;
    } finally {
      resultsEl.classList.remove("cs-loading");
    }
  }, 180);

  function bindSearchForm(formEl) {
    const input = formEl.querySelector(SELECTORS.input);
    const resultsEl = formEl.querySelector(SELECTORS.results);
    const overlay = SELECTORS.overlayById(SELECTORS.overlayId(input));

    if (!input || !resultsEl) return;

    // Abrir overlay al focus
    input.addEventListener("focus", () => {
      if ((input.value || "").trim().length >= 2) setActive(overlay, resultsEl, true);
    });

    input.addEventListener("input", () => onInput(formEl));

    // Cerrar al click overlay (tu overlay ya tiene onclick, pero reforzamos)
    overlay?.addEventListener("click", () => closeAll(formEl));

    // ESC cierra
    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeAll(formEl);
        input.blur();
      }
    });

    // Click fuera: cerrar
    document.addEventListener("click", (e) => {
      if (!formEl.contains(e.target)) closeAll(formEl);
    });
  }

  function init() {
    // Exponemos ruta para el fetch (Shopify routes)
    window.Shopify = window.Shopify || {};
    window.Shopify.routes = window.Shopify.routes || {};
    window.Shopify.routes.predictive_search_url =
      window.Shopify.routes.predictive_search_url || "/search/suggest";

    document.querySelectorAll(SELECTORS.root).forEach(bindSearchForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
